import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import {
  GoogleMap,
  Marker,
  InfoWindow,
  useJsApiLoader,
} from "@react-google-maps/api";
import "./styles/findUs.css";
import Nav from "./Nav";
import { MAPS_API_BASE_URL } from "../config";
import axios from "axios";

const GOOGLE_MAPS_LIBRARIES = ["places"];

const MAP_STYLE = [
  { elementType: "geometry", stylers: [{ color: "#f5f3f1" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#6b6b6b" }] },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#d9e8e8" }],
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ color: "#ffffff" }],
  },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
];

const BRAND_META = {
  tubbees: { label: "Tubbees", color: "#f2a2c4" },
  express: { label: "Tubbees Express", color: "#ff9f1c" },
};

const MAP_CONTAINER_STYLE = { width: "100%", height: "100%" };

const DEFAULT_CENTER = { lat: 55.860916, lng: -4.251433 };

// "isExpress: true" marks Tubbees Express. Everything else (including sites
// with no flag at all) is treated as a regular Tubbees site.
function getBrandKey(site) {
  return site.isExpress ? "express" : "tubbees";
}

function getDisplayName(site) {
  const base = site.name || "Tubbees";
  return site.isExpress ? `Tubbees Express – ${base}` : `Tubbees – ${base}`;
}

function getAddressLine(site) {
  const addr = site.Address;
  if (!addr) return "";
  const lines = [
    addr.addressLine1,
    addr.addressLine2,
    addr.addressLine3,
  ].filter(Boolean);
  return [...lines, addr.postcode].filter(Boolean).join(", ");
}

function getCoords(site) {
  const addr = site.Address;
  if (
    !addr ||
    typeof addr.latitude !== "number" ||
    typeof addr.longitude !== "number"
  ) {
    return null;
  }
  return { lat: addr.latitude, lng: addr.longitude };
}

function getHoursRows(site) {
  const bh = site.BusinessHours;
  if (!bh) return [];

  // if (bh.hours && Object.keys(bh.hours).length > 0) {
  //   return Object.entries(bh.hours).map(([day, time]) => ({ day, time }));
  // }

  const rows = [];

  if (bh.Mon_Thurs && (bh.Mon_Thurs.opens || bh.Mon_Thurs.closes)) {
    rows.push({
      day: "Mon – Thu",
      time: `${bh.Mon_Thurs.opens} – ${bh.Mon_Thurs.closes}`,
    });
  }

  if (bh.Fri_Sun && (bh.Fri_Sun.opens || bh.Fri_Sun.closes)) {
    rows.push({
      day: "Fri – Sun",
      time: `${bh.Fri_Sun.opens} – ${bh.Fri_Sun.closes}`,
    });
  }

  if (bh.Mon_Sat && (bh.Mon_Sat.opens || bh.Mon_Sat.closes)) {
    rows.push({
      day: "Mot – Sat",
      time: `${bh.Mon_Sat.opens} – ${bh.Mon_Sat.closes}`,
    });
  }

  if (bh.Sun && (bh.Sun.opens || bh.Sun.closes)) {
    rows.push({
      day: "Sun",
      time: `${bh.Sun.opens} – ${bh.Sun.closes}`,
    });
  }

  if (rows.length > 0) return rows;

  // if (bh.opens || bh.closes) {
  //   return [{ day: "Daily", time: `${bh.opens} – ${bh.closes}` }];
  // }
  return [];
}

// Extend this list as more utilities become relevant to show on a store card.
function getFeatures(site) {
  const features = [];
  if (site.sitIn) features.push("Dine-in");
  if (site.takeaway) features.push("Takeaway");

  const u = site.utilities || {};
  if (u.hasSlush) features.push("Slush");
  if (u.hasDesserts || u.tubbeesDessert) features.push("Desserts");
  if (u.hasIceCream) features.push("Ice cream");

  return features;
}

function getPrimaryPhone(site) {
  if (site.Contact && site.Contact.phoneNumber) return site.Contact.phoneNumber;
  if (site.Address && site.Address.phoneNumber) return site.Address.phoneNumber;
  return "";
}

function distanceMiles(userPos, coords) {
  const toRad = (v) => (v * Math.PI) / 180;
  const R = 3958.8;
  const dLat = toRad(coords.lat - userPos.lat);
  const dLng = toRad(coords.lng - userPos.lng);
  const lat1 = toRad(userPos.lat);
  const lat2 = toRad(coords.lat);
  const sinDLat = Math.sin(dLat / 2);
  const sinDLng = Math.sin(dLng / 2);
  const h =
    sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;
  return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

// ─── Store detail panel ──────────────────────────────────────────────────────

function StoreDetail({ store, onBack }) {
  const meta = BRAND_META[store.brand];
  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${store.lat},${store.lng}`;

  return (
    <div className="store-detail">
      <button className="detail-back" onClick={onBack}>
        ← Back to all stores
      </button>

      <header className="detail-header">
        <h2 className="detail-name">{store.name}</h2>

        {store.comingSoon && (
          <span className="detail-coming-soon">Coming soon</span>
        )}
      </header>

      <div className="detail-body">
        <div className="detail-info">
          <section className="detail-card">
            <h3 className="detail-section-title">
              Store information{" "}
              <span className="badge" style={{ backgroundColor: meta.color }}>
                {meta.label}
              </span>
            </h3>

            <div className="detail-group">
              <strong>Address</strong>
              <p className="detail-text">
                {store.address || "Address not available"}
              </p>
            </div>

            {store.phone && (
              <div className="detail-group">
                <strong>Phone</strong>

                <a href={`tel:${store.phone}`} className="detail-phone">
                  {store.phone}
                </a>
              </div>
            )}

            {store.hours.length > 0 && (
              <div className="detail-group">
                <strong>Opening hours</strong>

                <div className="detail-hours">
                  {store.hours.map((h) => (
                    <div key={h.day} className="hours-line">
                      <span className="hours-day">{h.day}</span>

                      <span className="hours-time">{h.time}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <a
              href={directionsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="card-link card-link--primary"
            >
              Get directions
            </a>
          </section>

          {store.features.length > 0 && (
            <section className="detail-card">
              <h3 className="detail-section-title">Facilities</h3>

              <div className="feature-row">
                {store.features.map((feature) => (
                  <span key={feature} className="feature-chip">
                    {feature}
                  </span>
                ))}
              </div>
            </section>
          )}
        </div>

        <div className="detail-map-wrapper">
          <h3 className="detail-section-title">Find us</h3>

          <div className="detail-map">
            <iframe
              title={`Map for ${store.name}`}
              width="100%"
              height="100%"
              loading="lazy"
              allowFullScreen
              style={{
                border: 0,
                borderRadius: 12,
              }}
              referrerPolicy="no-referrer-when-downgrade"
              src={`https://www.google.com/maps?q=${store.lat},${store.lng}&z=15&output=embed`}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function FindUs() {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyCA17XufYl3oox1qRT6aXvvM8JqmyvGC1I",
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const [activeFilter, setActiveFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [detailStoreId, setDetailStoreId] = useState(null); // drives the detail view
  const [userLocation, setUserLocation] = useState(null);
  const [postcodeInput, setPostcodeInput] = useState("");
  const [locating, setLocating] = useState(false);
  const [locateError, setLocateError] = useState(null);
  const [locationsData, setLocationsData] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const mapRef = useRef(null);
  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  useEffect(() => {
    axios
      .get(`${MAPS_API_BASE_URL}?site=Tubbees`)
      .then((res) => {
        setLocationsData(res.data.sites || []);
      })
      .catch((err) => {
        console.log(err);
        setLoadError(
          "We couldn't load store locations right now. Please try again shortly.",
        );
        setLocationsData([]);
      });
  }, []);

  const stores = useMemo(() => {
    if (!locationsData) return [];
    return locationsData
      .map((site) => {
        const coords = getCoords(site);
        if (!coords) return null; // skip sites with no usable location
        return {
          id: site._id,
          brand: getBrandKey(site),
          name: getDisplayName(site),
          address: getAddressLine(site),
          phone: getPrimaryPhone(site),
          lat: coords.lat,
          lng: coords.lng,
          hours: getHoursRows(site),
          features: getFeatures(site),
          comingSoon: !!site.comingSoon,
        };
      })
      .filter(Boolean);
  }, [locationsData]);

  const filteredStores = useMemo(() => {
    if (activeFilter === "all") return stores;
    return stores.filter((s) => s.brand === activeFilter);
  }, [stores, activeFilter]);

  const sortedStores = useMemo(() => {
    if (!userLocation) return filteredStores;
    return [...filteredStores]
      .map((s) => ({
        ...s,
        distance: distanceMiles(userLocation, { lat: s.lat, lng: s.lng }),
      }))
      .sort((a, b) => a.distance - b.distance);
  }, [filteredStores, userLocation]);

  const selectedStore = useMemo(
    () => stores.find((s) => s.id === selectedId) || null,
    [stores, selectedId],
  );

  const detailStore = useMemo(
    () => stores.find((s) => s.id === detailStoreId) || null,
    [stores, detailStoreId],
  );

  const mapCenter = selectedStore
    ? { lat: selectedStore.lat, lng: selectedStore.lng }
    : userLocation || DEFAULT_CENTER;

  const handleSelectStore = (store) => {
    setSelectedId(store.id);
    if (mapRef.current) {
      mapRef.current.panTo({ lat: store.lat, lng: store.lng });
      mapRef.current.setZoom(14);
    }
  };

  const handleOpenDetail = (store, e) => {
    e.stopPropagation();
    setDetailStoreId(store.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCloseDetail = () => {
    setDetailStoreId(null);
  };

  const handleUseMyLocation = () => {
    setLocateError(null);
    if (!navigator.geolocation) {
      setLocateError(
        "Your browser doesn't support location lookup. Try entering a postcode instead.",
      );
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLocation({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
        setLocating(false);
      },
      () => {
        setLocateError(
          "We couldn't get your location. Try entering a postcode instead.",
        );
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };

  const handlePostcodeSearch = async (e) => {
    e.preventDefault();
    setLocateError(null);
    if (!postcodeInput.trim()) return;

    if (!window.google || !window.google.maps) {
      setLocateError("Map isn't ready yet — give it a second and try again.");
      return;
    }

    setLocating(true);
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: `${postcodeInput}, UK` }, (results, status) => {
      setLocating(false);
      if (status === "OK" && results[0]) {
        const loc = results[0].geometry.location;
        setUserLocation({ lat: loc.lat(), lng: loc.lng() });
      } else {
        setLocateError(
          "We couldn't find that postcode. Double check it and try again.",
        );
      }
    });
  };

  return (
    <div className="page">
      <Nav />

      <div className="hero">
        <h1 className="h1">Tubbees &amp; Tubbees Express, near you</h1>
        <p className="hero-sub">
          Search by postcode or use your location to find your closest Tubbees
          store.
        </p>
      </div>

      {!detailStore && (
        <div className="search-bar">
          <form onSubmit={handlePostcodeSearch} className="search-form">
            <input
              type="text"
              value={postcodeInput}
              onChange={(e) => setPostcodeInput(e.target.value)}
              placeholder="Enter your postcode"
              className="search-input"
              aria-label="Postcode"
            />
            <button type="submit" className="search-button" disabled={locating}>
              {locating ? "Searching…" : "Search"}
            </button>
          </form>

          <button
            type="button"
            onClick={handleUseMyLocation}
            className="locate-button"
            disabled={locating}
          >
            📍 Use my location
          </button>

          <div
            className="filter-group"
            role="tablist"
            aria-label="Filter store type"
          >
            {[
              { key: "all", label: "All stores" },
              { key: "tubbees", label: "Tubbees" },
              { key: "express", label: "Tubbees Express" },
            ].map((f) => (
              <button
                key={f.key}
                role="tab"
                aria-selected={activeFilter === f.key}
                onClick={() => setActiveFilter(f.key)}
                className={`filter-tab${activeFilter === f.key ? " filter-tab--active" : ""}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {locateError && <div className="error-banner">{locateError}</div>}
      {loadError && <div className="error-banner">{loadError}</div>}

      {detailStore ? (
        <div className="body">
          <StoreDetail store={detailStore} onBack={handleCloseDetail} />
        </div>
      ) : (
        <div className="body">
          {/* Store list */}
          <div className="list-column">
            {locationsData === null && (
              <p className="empty-state">Loading stores…</p>
            )}

            {locationsData !== null && sortedStores.length === 0 && (
              <p className="empty-state">No stores match that filter yet.</p>
            )}

            {sortedStores.map((store) => {
              const meta = BRAND_META[store.brand];
              const isSelected = store.id === selectedId;
              return (
                <button
                  key={store.id}
                  onClick={() => handleSelectStore(store)}
                  className={`store-card${isSelected ? " store-card--selected" : ""}`}
                >
                  <div className="card-top-row">
                    <span
                      className="badge"
                      style={{ backgroundColor: meta.color }}
                    >
                      {meta.label}
                    </span>
                    {typeof store.distance === "number" && (
                      <span className="distance-text">
                        {store.distance.toFixed(1)} mi
                      </span>
                    )}
                  </div>

                  <h3 className="store-name">{store.name}</h3>
                  <p className="store-address">{store.address}</p>

                  {store.hours.length > 0 && (
                    <div className="hours-row">
                      {store.hours.map((h) => (
                        <div key={h.day} className="hours-line">
                          <span className="hours-day">{h.day}</span>
                          <span className="hours-time">{h.time}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {store.features.length > 0 && (
                    <div className="feature-row">
                      {store.features.map((f) => (
                        <span key={f} className="feature-chip">
                          {f}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="card-actions">
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${store.lat},${store.lng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="card-link card-link--primary"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Get directions
                    </a>
                    <button
                      className="card-link"
                      onClick={(e) => handleOpenDetail(store, e)}
                    >
                      Store details
                    </button>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Map */}
          <div className="map-column">
            {isLoaded ? (
              <GoogleMap
                mapContainerStyle={MAP_CONTAINER_STYLE}
                center={mapCenter}
                zoom={selectedStore || userLocation ? 13 : 9}
                onLoad={onMapLoad}
                options={{
                  disableDefaultUI: true,
                  zoomControl: true,
                  styles: MAP_STYLE,
                }}
              >
                {sortedStores.map((store) => {
                  const meta = BRAND_META[store.brand];
                  return (
                    <Marker
                      key={store.id}
                      position={{ lat: store.lat, lng: store.lng }}
                      onClick={() => handleSelectStore(store)}
                      icon={{
                        path: window.google.maps.SymbolPath.CIRCLE,
                        fillColor: meta.color,
                        fillOpacity: 1,
                        strokeColor: "#ffffff",
                        strokeWeight: 2,
                        scale: store.id === selectedId ? 11 : 8,
                      }}
                    />
                  );
                })}

                {userLocation && (
                  <Marker
                    position={userLocation}
                    icon={{
                      path: window.google.maps.SymbolPath.CIRCLE,
                      fillColor: "#1a1a1a",
                      fillOpacity: 1,
                      strokeColor: "#ffffff",
                      strokeWeight: 2,
                      scale: 6,
                    }}
                    title="Your location"
                  />
                )}

                {selectedStore && (
                  <InfoWindow
                    position={{
                      lat: selectedStore.lat,
                      lng: selectedStore.lng,
                    }}
                    onCloseClick={() => setSelectedId(null)}
                  >
                    <div style={{ maxWidth: 200 }}>
                      <strong>{selectedStore.name}</strong>
                      <p style={{ margin: "4px 0 0", fontSize: 13 }}>
                        {selectedStore.address}
                      </p>
                    </div>
                  </InfoWindow>
                )}
              </GoogleMap>
            ) : (
              <div className="map-loading">Loading map…</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
